export class Accordion {
  /**
   * Represents a dropdown accordion
   *
   * Uses Custom Element <accordion-group>
   *
   * @constructor
   * @param {HTMLElement} container parent custom element
   * @property {HTMLElement} featuredImage - sticky image being displayed (optional)
   * @property {HTMLElement} activePanel - panel which is currently open
   * @property {HTMLElement} activeIcon - icon for active panel
   */
  constructor(container) {
    this.container = container;
    this.featuredImage = this.container
      ? this.container.querySelector("[data-default-src]")
      : null;
    this.activePanel = null;
    this.activeIcon = null;
    this._init();
  }

  /**
    Checks if any panels exist inside container element and adds a click element to each panel button

    @memberof Accordion
    @returns {void}
    @throws {Error} If the container is not found or if there are no panels found in the container
  */
  _buildPanels() {
    const allPanels = this.container.querySelectorAll("button");

    if (!allPanels || !allPanels.length) {
      return
    }

    const initiallyOpenPanel = [...allPanels].find(
      (panel) => panel.getAttribute("data-open") === "true"
    );

    if (initiallyOpenPanel) {
      const targetId = initiallyOpenPanel.dataset.panel;
      this.activePanel = document.getElementById(targetId);
      this.activeIcon = initiallyOpenPanel.querySelector("svg");
      this._openPanel();
    }

    allPanels.forEach((panel) => {
      panel.addEventListener("click", this._handleClick);
    });
  }

  /**
    Update the featured image for a given element.
    @param {string|boolean} newImage - The URL of the new image to be set as featured image. If set to false, the default image will be displayed.
    @returns {void}
  */
  _updateFeaturedImage = (newImage = false) => {
    if (this.featuredImage) {
      if (newImage) {
        this.featuredImage.style.opacity = "0";
        this.featuredImage.src = newImage;
        $(this.featuredImage).imagesLoaded(() => {
          this.featuredImage.style.opacity = "1";
        });
      } else {
        this.featuredImage.style.opacity = "0";
        setTimeout(() => {
          // if all panels close display default image set as data attribute on image element
          this.featuredImage.src = this.featuredImage.dataset.defaultSrc;
          this.featuredImage.style.opacity = "1";
        }, 300);
      }
    }
  };

  /**
    Updates the featured image and opens the panel by rotating the active icon and setting the active panel's height to the height of its children.
    @param {string|boolean} newImage - Whether a new image is being added or not. Defaults to false.
  */
  _openPanel = (newImage) => {
    this._updateFeaturedImage(newImage);


    this.activeIcon.style.transform = "rotate(180deg)";
    // this.activePanel.style.height = `${this.activePanel.children[0].offsetHeight}px`;
    // this.activePanel.style.opacity = '1';
    // this.activePanel.style.blockSize = 'auto';
    this.activePanel.style.display = 'block';
    this.activePanel.setAttribute("aria-expanded", "true");
    const wrapper = this.activePanel.closest('.accordion-item');
    if (wrapper) {
      wrapper.setAttribute('aria-expanded', 'true');
    }
  };

  /**
    Closes the active panel by rotating the active icon back to its original position and setting the active panel's height to 0.
  */
  _closePanel = () => {
    if (!this.activePanel) {
      return;
    }
    this.activeIcon.style.transform = "rotate(0deg)";
    // this.activePanel.style.height = `0px`;
    // this.activePanel.style.opacity = '0';
    // this.activePanel.style.blockSize = '0';
    this.activePanel.style.display = 'none';
    this.activePanel.setAttribute("aria-expanded", "false");
    const wrapper = this.activePanel.closest('.accordion-item');
    if (wrapper) {
      wrapper.setAttribute('aria-expanded', 'false');
    }

  };

  /**
    Toggles the active panel between open and closed states by checking the current height of the active panel and calling either _openPanel or _resetPanels.
  */
  _togglePanel = () => {
    this.activePanel.style.height =
      this.activePanel.style.height === "0px" || this.activePanel.style.display === 'none'
        ? this._openPanel()
        : this._resetPanels();
  };

  /**
    Resets the panels by updating the featured image to its default value, closing the active panel, and setting the active panel to false.
  */
  _resetPanels = () => {
    this._updateFeaturedImage();
    this._closePanel();
    this.activePanel = false;
  };

  /**
    Handles the click event on a panel by checking whether the selected panel is the active panel, and then calling _togglePanel or _closePanel and _openPanel as necessary.
    @param {Event} e - The click event object.
  */
  _handleClick = (e) => {
    const selectedPanel = e.currentTarget;
    const targetId = selectedPanel.dataset.panel;
    const newImage = selectedPanel.dataset.image;
    console.log(targetId, "targetId");

    if (this.activePanel && targetId === this.activePanel.id) {
      this._togglePanel();
      return;
    }

    this._closePanel();
    this._updateActivePanel(targetId, selectedPanel);
    this._openPanel(newImage);
  };

  /**
    Updates the active panel and active icon based on the selected panel's ID and active icon element.
    @param {string} targetId - The ID of the panel to make active.
    @param {HTMLElement} selectedPanel - The panel element that was clicked.
  */
  _updateActivePanel = (targetId, selectedPanel) => {
    this.activePanel = document.getElementById(targetId);
    this.activeIcon = selectedPanel.querySelector("svg");
  };

  /**
   * Initialize the accordion component by checking if the container element exists and building its panels.
   * @throws {Error} If the container element is not found.
   * @returns {void}
   */
  _init() {
    if (!this.container) {
      throw new Error("Accordion container not found");
    }
    this._buildPanels();
  }
}
